import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, addDoc, onSnapshot, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import LibraryManager from '../components/LibraryManager'; 
import InteractiveBookViewer from '../components/InteractiveBooks/InteractiveBookViewer';

// 🔥 ALL 35 ADVANCED THEMES ADDED FOR THE MASTER LIBRARY
const TACTICAL_TOPICS = [
    { id: 1, name: "Mate in 1", icon: "🎯", tag: "mateIn1", desc: "Find the immediate knockout punch." },
    { id: 2, name: "Mate in 2", icon: "♟️", tag: "mateIn2", desc: "Calculate forced 2-move checkmates." },
    { id: 3, name: "Mate in 3+", icon: "🔥", tag: "mateIn3", desc: "Complex checkmate combinations." },
    { id: 4, name: "Back-Rank Mate", icon: "🚪", tag: "backRankMate", desc: "Exploit weak back-rank defenses." },
    { id: 5, name: "Smothered Mate", icon: "🗜️", tag: "smotheredMate", desc: "Trap the king behind its own pieces." },
    { id: 6, name: "Anastasia's Mate", icon: "🏰", tag: "anastasiaMate", desc: "Rook and Knight deadly combo." },
    { id: 7, name: "Arabian Mate", icon: "🐪", tag: "arabianMate", desc: "Corner the king with Rook and Knight." },
    { id: 8, name: "The Fork", icon: "🔱", tag: "fork", desc: "Attack two pieces at the same time." },
    { id: 9, name: "The Pin", icon: "📌", tag: "pin", desc: "Paralyze the opponent's pieces." },
    { id: 10, name: "The Skewer", icon: "🍢", tag: "skewer", desc: "X-Ray attacks through high-value pieces." },
    { id: 11, name: "Discovered Attack", icon: "👻", tag: "discoveredAttack", desc: "Move a piece to reveal a hidden attack." },
    { id: 12, name: "Double Check", icon: "‼️", tag: "doubleCheck", desc: "The most powerful check in chess." },
    { id: 13, name: "Removing the Defender", icon: "💥", tag: "capturingDefender", desc: "Destroy the piece guarding the target." },
    { id: 14, name: "Deflection", icon: "🧲", tag: "deflection", desc: "Force a key defender away." },
    { id: 15, name: "Attraction", icon: "🎣", tag: "attraction", desc: "Lure a piece to a fatal square." },
    { id: 16, name: "Clearance", icon: "🧹", tag: "clearance", desc: "Clear a square or diagonal for an attack." },
    { id: 17, name: "Intermezzo", icon: "⚡", tag: "intermezzo", desc: "The surprising in-between move." },
    { id: 18, name: "Interference", icon: "🚧", tag: "interference", desc: "Block the line of a defending piece." },
    { id: 19, name: "X-Ray Attack", icon: "🔫", tag: "xRayAttack", desc: "Attack through an opponent's piece." },
    { id: 20, name: "Zugzwang", icon: "🛑", tag: "zugzwang", desc: "Force the opponent to make a bad move." },
    { id: 21, name: "Capture Free Piece", icon: "🍎", tag: "hangingPiece", desc: "Spot and take undefended pieces." },
    { id: 22, name: "Defensive Move", icon: "🛡️", tag: "defensiveMove", desc: "Escape check or prevent checkmate." },
    { id: 23, name: "Trapped Piece", icon: "🕸️", tag: "trappedPiece", desc: "Cut off a piece's escape squares." },
    { id: 24, name: "Winning Advantage", icon: "📈", tag: "crushing", desc: "Convert a superior position." },
    { id: 25, name: "Sacrifice", icon: "🔥", tag: "sacrifice", desc: "Give up material for a greater advantage." },
    { id: 26, name: "Exposed King", icon: "🎯", tag: "exposedKing", desc: "Attack a king with no pawn shield." },
    { id: 27, name: "Endgame Master", icon: "⏳", tag: "endgame", desc: "Master the final phase of the game." },
    { id: 28, name: "Pawn Endgame", icon: "♟️", tag: "pawnEndgame", desc: "King and pawn combinations." },
    { id: 29, name: "Rook Endgame", icon: "♖", tag: "rookEndgame", desc: "The most common and tricky endgames." },
    { id: 30, name: "Opening Traps", icon: "🪤", tag: "opening", desc: "Punish early mistakes in the opening." },
    { id: 31, name: "Middlegame Clash", icon: "⚔️", tag: "middlegame", desc: "Complex positional and tactical battles." },
    { id: 32, name: "Advanced Pawn", icon: "🚀", tag: "advancedPawn", desc: "Push passed pawns to victory." },
    { id: 33, name: "Promotion Tactics", icon: "👑", tag: "promotion", desc: "Tactics involving pawn promotion." },
    { id: 34, name: "Underpromotion", icon: "🐴", tag: "underPromotion", desc: "Promote to a Knight, Bishop, or Rook." },
    { id: 35, name: "En Passant", icon: "👻", tag: "enPassant", desc: "The tricky pawn capture rule." }
];

export default function Library({ user }) {
    const [libTab, setLibTab] = useState('PREBUILT');
    const [allBatches, setAllBatches] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [activeTopic, setActiveTopic] = useState(null);
    const [assignModal, setAssignModal] = useState(null); 
    const [assignTarget, setAssignTarget] = useState('BATCH'); 
    const [selectedTargetId, setSelectedTargetId] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [loadToClassModal, setLoadToClassModal] = useState(null); // { topic, levelNum }

    useEffect(() => {
        if (!user) return;
        const safeEmail = user.email?.toLowerCase() || '';
        const role = (user.role || '').toUpperCase();

        let qBatches;
        if (['OWNER', 'ADMIN'].includes(role)) {
            qBatches = collection(db, "batches");
        } else {
            qBatches = query(collection(db, "batches"), where("coachEmail", "==", safeEmail));
        }

        const unsubBatches = onSnapshot(qBatches, (snap) => {
            const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setAllBatches(list);
        });

        const qUsers = query(collection(db, "users"), where("role", "==", "STUDENT"));
        const unsubUsers = onSnapshot(qUsers, (snap) => {
            const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setAllUsers(list);
        });

        return () => { unsubBatches(); unsubUsers(); };
    }, [user]);

    if (!user) return <div style={{color: 'var(--text-primary)', padding: '20px' }}>Loading Library...</div>;
    if (user.role === 'STUDENT') {
        return (
            <div style={{padding: '40px', textAlign: 'center', color: '#ef4444' }}>
                <h2>🚫 Access Denied</h2>
                <p>Only Coaches and Owners can access the Master Library.</p>
            </div>
        );
    }

    const studentsList = allUsers.filter(u => u.role === 'STUDENT');
    const safeEmail = user?.email?.toLowerCase() || '';
    const myBatches = allBatches;

    const handleAssign = async (e) => {
        e.preventDefault();
        if (!selectedTargetId) return toast.error("Please select a Batch or Student!");
        if (!dueDate) return toast.error("Please set a Due Date!");

        // 🔥 CRITICAL FIX: Ab correct format mein save hoga taaki engine read kar sake
        const smartLevelId = `TACTICS_${activeTopic.tag}_${assignModal}`;

        let targetData = {};
        if (assignTarget === 'BATCH') {
            const batchInfo = allBatches.find(b => b.id === selectedTargetId);
            targetData = { assignTarget: 'BATCH', batchId: selectedTargetId, batchTitle: batchInfo.title };
        } else {
            const studentInfo = studentsList.find(s => s.email === selectedTargetId);
            targetData = { assignTarget: 'STUDENT', studentEmail: selectedTargetId, studentName: studentInfo.name || selectedTargetId.split('@')[0] };
        }

        const loadingId = toast.loading("Deploying Tactical Assignment...");
        try {
            await addDoc(collection(db, "homeworks"), {
                title: `${activeTopic.icon} ${activeTopic.name} - Level ${assignModal}`,
                levelId: smartLevelId, 
                chapterId: 'LIBRARY', 
                ...targetData,
                assignedBy: safeEmail,
                dueDate: dueDate,
                createdAt: new Date().toISOString()
            });
            toast.success("Assignment Deployed Successfully!", { id: loadingId });
            setAssignModal(null); 
            setSelectedTargetId('');
            setDueDate('');
        } catch (error) {
            toast.error("Failed to assign.", { id: loadingId });
        }
    };

    const handleLoadToClass = async () => {
        if (!loadToClassModal) return;
        const { topic, levelNum } = loadToClassModal;
        const safeEmail = user?.email?.toLowerCase() || '';
        const loadingId = toast.loading("Finding your live class...");
        try {
            // Find coach's active LIVE class
            const q = query(collection(db, "classes"), where("teacherId", "==", safeEmail), where("status", "==", "LIVE"));
            const snap = await getDocs(q);
            if (snap.empty) {
                toast.error("No active live class found! Start a class first.", { id: loadingId });
                setLoadToClassModal(null);
                return;
            }
            const classDoc = snap.docs[0];
            const classId = classDoc.id;
            // Load the tactical puzzle level info to the class
            const levelId = `TACTICS_${topic.tag}_${levelNum}`;
            await updateDoc(doc(db, "classes", classId), {
                classworkTitle: `${topic.icon} ${topic.name} — Level ${levelNum}`,
                classworkLevelId: levelId,
                classworkType: 'TACTICAL_PUZZLE',
                classMode: 'CLASSWORK'
            });
            toast.success(`"${topic.name} Level ${levelNum}" loaded to live class! 🚀`, { id: loadingId });
            setLoadToClassModal(null);
        } catch (e) {
            console.error(e);
            toast.error("Failed to load to class.", { id: loadingId });
        }
    };

    return (
        <div style={{padding: '20px', minHeight: '100%', width: '100%', boxSizing: 'border-box', maxWidth: '1400px', margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
            
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', background: 'linear-gradient(90deg, var(--bg-primary), var(--bg-primary))', padding: '20px 30px', borderRadius: '16px', border: '1px solid rgba(163, 177, 198, 0.4)', boxShadow: 'var(--panel-shadow)' }}>
                <h1 style={{color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '15px', fontSize: '28px' }}>
                    📚 HQ Library
                </h1>
                
                <div style={{display: 'flex', gap: '10px', background: 'var(--bg-primary)', padding: '8px', borderRadius: '12px', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                    <button 
                        onClick={() => setLibTab('PREBUILT')} 
                        style={{padding: '10px 20px', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', transition: '0.2s', background: libTab === 'PREBUILT' ? '#3b82f6' : 'transparent', color: libTab === 'PREBUILT' ? 'white' : 'var(--text-secondary)' }}
                    >
                        🧩 Tactical Syllabus
                    </button>
                    <button 
                        onClick={() => setLibTab('BOOKS')} 
                        style={{padding: '10px 20px', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', transition: '0.2s', background: libTab === 'BOOKS' ? '#10b981' : 'transparent', color: libTab === 'BOOKS' ? 'black' : 'var(--text-secondary)' }}
                    >
                        📖 Interactive Books
                    </button>
                    <button 
                        onClick={() => setLibTab('CUSTOM')} 
                        style={{padding: '10px 20px', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', transition: '0.2s', background: libTab === 'CUSTOM' ? '#f59e0b' : 'transparent', color: libTab === 'CUSTOM' ? 'black' : 'var(--text-secondary)' }}
                    >
                        🛠️ Custom PGN Studio
                    </button>
                </div>
            </div>

            {libTab === 'BOOKS' && (
                <div style={{height: 'calc(100% - 120px)', animation: 'fadeIn 0.4s ease-in-out' }}>
                    <InteractiveBookViewer user={user} />
                </div>
            )}

            {libTab === 'CUSTOM' && (
                <div style={{height: 'calc(100% - 120px)', animation: 'fadeIn 0.4s ease-in-out' }}>
                    <LibraryManager userProfile={user} />
                </div>
            )}

            {libTab === 'PREBUILT' && (
                <div style={{animation: 'fadeIn 0.4s ease-in-out' }}>
                    {activeTopic ? (
                        <div>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', background: 'var(--bg-primary)', padding: '20px 25px', borderRadius: '16px', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    <div style={{fontSize: '40px', background: 'rgba(59, 130, 246, 0.1)', padding: '10px 15px', borderRadius: '12px', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                                        {activeTopic.icon}
                                    </div>
                                    <div>
                                        <h2 style={{color: 'var(--text-primary)', margin: '0 0 5px 0' }}>{activeTopic.name}</h2>
                                        <p style={{color: '#10b981', margin: 0, fontWeight: 'bold', fontSize: '13px' }}>Assign Difficulty Levels</p>
                                    </div>
                                </div>
                                <button onClick={() => setActiveTopic(null)} style={{padding: '10px 20px', background: 'rgba(163, 177, 198, 0.4)', color: 'var(--text-primary)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                                    ⬅ Back to Syllabus
                                </button>
                            </div>

                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(levelNum => {
                                    const ratingTarget = 500 + (levelNum * 100); 
                                    return (
                                        <div key={levelNum} style={{background: 'var(--bg-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', borderRadius: '16px', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.2)', gap: '12px', flexWrap: 'wrap' }}>
                                            <div style={{flex: 1, minWidth: '150px'}}>
                                                <div style={{color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '5px' }}>Difficulty Level {levelNum}</div>
                                                <h3 style={{color: 'var(--text-primary)', margin: '0 0 5px 0', fontSize: '20px' }}>Target Rating: ~{ratingTarget}</h3>
                                                <div style={{color: '#64748b', fontSize: '13px' }}>20 Handpicked Puzzles</div>
                                            </div>
                                            <div style={{display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'stretch'}}>
                                                <button 
                                                    onClick={() => setAssignModal(levelNum)}
                                                    style={{background: '#3b82f6', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 10px rgba(59, 130, 246, 0.4)', fontSize: '13px', whiteSpace: 'nowrap' }}
                                                >
                                                    📋 Assign Homework
                                                </button>
                                                <button 
                                                    onClick={() => setLoadToClassModal({ topic: activeTopic, levelNum })}
                                                    style={{background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 10px rgba(16, 185, 129, 0.4)', fontSize: '13px', whiteSpace: 'nowrap' }}
                                                >
                                                    🎓 Load to Class
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '25px', paddingBottom: '40px' }}>
                            {TACTICAL_TOPICS.map(topic => (
                                <div 
                                    key={topic.id} 
                                    onClick={() => setActiveTopic(topic)}
                                    style={{background: 'linear-gradient(135deg, var(--bg-primary), var(--bg-primary))', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '25px', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', transition: 'all 0.3s', boxShadow: '0 10px 20px rgba(0,0,0,0.3)', position: 'relative', overflow: 'hidden' 
                                    }}
                                    onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.borderColor = '#3b82f6'; }}
                                    onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                                >
                                    <div style={{fontSize: '50px', marginBottom: '15px', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.5))' }}>{topic.icon}</div>
                                    <h3 style={{color: 'var(--text-primary)', margin: '0 0 10px 0', fontSize: '22px', textAlign: 'center' }}>{topic.name}</h3>
                                    <p style={{color: 'var(--text-secondary)', margin: '0 0 15px 0', fontSize: '13px', textAlign: 'center', minHeight: '35px' }}>{topic.desc}</p>
                                    <div style={{background: 'var(--bg-primary)', padding: '8px 20px', borderRadius: '20px', color: '#3b82f6', fontSize: '12px', fontWeight: 'bold', border: '1px solid rgba(163, 177, 198, 0.4)' }}>12 Levels (600-1800)</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {assignModal && (
                <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, backdropFilter: 'blur(5px)' }}>
                    <div style={{background: 'var(--bg-primary)', padding: '30px', borderRadius: '20px', width: '450px', maxWidth: '90%', border: '2px solid #3b82f6', boxShadow: '0 20px 50px rgba(0,0,0,0.5)', animation: 'popIn 0.3s ease-out' }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{color: 'var(--text-primary)', margin: 0 }}>Assign Tactical Homework</h2>
                            <button onClick={() => setAssignModal(null)} style={{background: 'transparent', border: 'none', color: '#ef4444', fontSize: '20px', cursor: 'pointer' }}>✖</button>
                        </div>

                        <div style={{background: 'rgba(59, 130, 246, 0.1)', padding: '15px', borderRadius: '10px', border: '1px dashed #3b82f6', marginBottom: '20px', color: '#1d4ed8', fontSize: '14px', textAlign: 'center', fontWeight: 'bold' }}>
                            Deploying Task: <strong style={{color: 'var(--text-primary)'}}>{activeTopic.icon} {activeTopic.name} - Level {assignModal}</strong>
                        </div>

                        <div style={{display: 'flex', gap: '10px', marginBottom: '20px', background: 'var(--bg-primary)', padding: '5px', borderRadius: '10px' }}>
                            <button onClick={() => setAssignTarget('BATCH')} style={{flex: 1, padding: '10px', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', background: assignTarget === 'BATCH' ? '#3b82f6' : 'transparent', color: assignTarget === 'BATCH' ? 'white' : 'var(--text-secondary)' }}>🏫 To Batch</button>
                            <button onClick={() => setAssignTarget('STUDENT')} style={{flex: 1, padding: '10px', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', background: assignTarget === 'STUDENT' ? '#f59e0b' : 'transparent', color: assignTarget === 'STUDENT' ? 'black' : 'var(--text-secondary)' }}>👤 To Student</button>
                        </div>

                        <form onSubmit={handleAssign} style={{display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {assignTarget === 'BATCH' ? (
                                <div>
                                    <label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Select Target Batch</label>
                                    <select value={selectedTargetId} onChange={e => setSelectedTargetId(e.target.value)} required style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                                        <option value="">-- Select Batch --</option>
                                        {myBatches.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                                    </select>
                                </div>
                            ) : (
                                <div>
                                    <label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Select Target Student</label>
                                    <select value={selectedTargetId} onChange={e => setSelectedTargetId(e.target.value)} required style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                                        <option value="">-- Select Student --</option>
                                        {studentsList.map(s => <option key={s.email} value={s.email}>{s.name || s.username || s.email.split('@')[0]} (@{s.username || s.email.split('@')[0]})</option>)}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Due Date</label>
                                <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} required style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', boxSizing: 'border-box', colorScheme: 'dark' }} />
                            </div>

                            <button type="submit" style={{marginTop: '10px', padding: '15px', background: '#10b981', color: 'black', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)' }}>
                                ✅ Confirm Assignment
                            </button>
                        </form>
                    </div>
                </div>
            )}
            {loadToClassModal && (
                <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, backdropFilter: 'blur(5px)' }}>
                    <div style={{background: 'var(--bg-primary)', padding: '30px', borderRadius: '20px', width: '420px', maxWidth: '90%', border: '2px solid #10b981', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{color: 'var(--text-primary)', margin: 0, fontSize: '20px' }}>🎓 Load to Live Class</h2>
                            <button onClick={() => setLoadToClassModal(null)} style={{background: 'transparent', border: 'none', color: '#ef4444', fontSize: '20px', cursor: 'pointer' }}>✖</button>
                        </div>

                        <div style={{background: 'rgba(16, 185, 129, 0.1)', padding: '15px', borderRadius: '10px', border: '1px dashed #10b981', marginBottom: '20px', color: '#059669', fontSize: '14px', textAlign: 'center', fontWeight: 'bold' }}>
                            <div style={{fontSize: '28px', marginBottom: '8px'}}>{loadToClassModal.topic.icon}</div>
                            <div>{loadToClassModal.topic.name} — Level {loadToClassModal.levelNum}</div>
                            <div style={{fontSize: '12px', marginTop: '4px', color: 'var(--text-secondary)'}}>This will be set as classwork in your active live class.</div>
                        </div>

                        <div style={{background: 'rgba(245, 158, 11, 0.1)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(245,158,11,0.3)', marginBottom: '20px', fontSize: '13px', color: '#d97706' }}>
                            ⚠️ You must have an <strong>active LIVE class</strong> running. Students will see this puzzle set immediately.
                        </div>

                        <div style={{display: 'flex', gap: '12px'}}>
                            <button onClick={() => setLoadToClassModal(null)} style={{flex: 1, padding: '12px', background: 'transparent', color: 'var(--text-primary)', border: '1px solid #64748b', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer'}}>Cancel</button>
                            <button onClick={handleLoadToClass} style={{flex: 2, padding: '12px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)', fontSize: '15px' }}>
                                🚀 Load to Live Class Now
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
